import { useSignUp as useClerkSignUp } from "@clerk/clerk-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { getClerkErrorMessage } from "@/utils/clerk-helper";

type SignUpStep = "email" | "verifying" | "ticket";

interface SignUpFlowState {
  step: SignUpStep;
  email: string;
  code: string;
  isLoading: boolean;
  error: string | null;
}

/**
 * useSignUpFlow — wraps Clerk's useSignUp() to drive the two-step passwordless
 * OTP sign-up flow shown in the design demo:
 *
 *   Step 1 (email):     User enters their work email → we call signUp.create()
 *                        and then prepareEmailAddressVerification() to send OTP.
 *   Step 2 (verifying): User enters the code → we call
 *                        attemptEmailAddressVerification() to verify,
 *                        then activate the session and redirect to /onboarding.
 *
 * Also supports OAuth (Google / Microsoft) via authenticateWithRedirect().
 *
 * Invitation ticket flow:
 *   When the URL contains `__clerk_ticket`, the hook immediately calls
 *   signUp.create({ strategy: 'ticket', ticket }) on mount. Clerk validates the
 *   token server-side and returns a completed session — the invited user's email
 *   is pre-verified; no OTP step is needed. The page is informed via the
 *   `isTicketFlow` and `ticketComplete` flags so it can show a spinner and then
 *   navigate to /onboarding without ever rendering the email/code form.
 */
export function useSignUpFlow() {
  const { signUp, setActive, isLoaded } = useClerkSignUp();

  const [state, setState] = useState<SignUpFlowState>({
    step: "email",
    email: "",
    code: "",
    isLoading: false,
    error: null,
  });

  // Signals that the ticket-based session is active and the page should navigate.
  const ticketCompleteRef = useRef(false);
  // Re-render trigger so the page picks up ticketComplete = true.
  const [ticketComplete, setTicketComplete] = useState(false);

  const setEmail = useCallback((email: string) => {
    setState((prev) => ({ ...prev, email, error: null }));
  }, []);

  const setCode = useCallback((code: string) => {
    setState((prev) => ({ ...prev, code, error: null }));
  }, []);

  // Use refs to avoid recreating callbacks on every keystroke
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  /**
   * Invitation ticket flow — runs once on mount when Clerk is ready.
   *
   * If `__clerk_ticket` is present in the URL, consume it immediately via the
   * 'ticket' strategy. For a passwordless (email OTP) Clerk instance the result
   * is always 'complete' — the session is created and the invited user's email is
   * already verified. The page renders a spinner until `ticketComplete` is true,
   * then navigates to /onboarding (which handles invited users correctly by
   * detecting `alreadyOnboarded` and redirecting to the home page).
   */
  const ticketCheckedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || !signUp || !setActive) return;
    if (ticketCheckedRef.current) return;

    const ticket = new URLSearchParams(window.location.search).get("__clerk_ticket");
    if (!ticket) return;
    ticketCheckedRef.current = true;

    setState((prev) => ({ ...prev, step: "ticket", isLoading: true, error: null }));

    void signUp
      .create({ strategy: "ticket", ticket })
      .then(async (result) => {
        if (result.status === "complete" && result.createdSessionId) {
          // The ticket is consumed and the session is ready. Activate it so
          // Clerk's auth context is up-to-date before the page navigates.
          await setActive({ session: result.createdSessionId });
          setState((prev) => ({ ...prev, isLoading: false }));
          ticketCompleteRef.current = true;
          setTicketComplete(true);
        } else {
          // Unexpected: ticket was accepted but Clerk still needs additional
          // fields (e.g. a password). This would indicate a Clerk Dashboard
          // mis-configuration for this instance.
          const missing = result.missingFields?.join(", ") ?? "unknown fields";
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: `Your invitation was accepted but additional sign-up requirements are configured (${missing}). Please contact support.`,
          }));
        }
      })
      .catch((e) => {
        // The ticket was invalid, already used, or expired. Fall back to the
        // normal email step so the user can still attempt a regular sign-up,
        // and show a contextual error explaining what happened.
        setState((prev) => ({
          ...prev,
          step: "email",
          isLoading: false,
          error: getClerkErrorMessage(
            e,
            "Your invitation link is invalid or has already been used. Please request a new invitation, or sign up below.",
          ),
        }));
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, signUp, setActive]);

  /**
   * Step 1 → Step 2: create the sign-up and send the OTP email.
   */
  const sendCode = useCallback(async () => {
    if (!isLoaded || !signUp) return;
    if (stateRef.current.isLoading) return;

    const trimmedEmail = stateRef.current.email.trim();
    if (!trimmedEmail) {
      setState((prev) => ({ ...prev, error: "Please enter your work email." }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await signUp.create({ emailAddress: trimmedEmail });
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setState((prev) => ({ ...prev, step: "verifying", isLoading: false }));
    } catch (e) {
      setState((prev) => ({ ...prev, isLoading: false, error: getClerkErrorMessage(e) }));
    }
  }, [isLoaded, signUp]);

  /**
   * Step 2 → done: verify the OTP code and activate the session.
   * Returns true on success so the caller can redirect to /onboarding.
   */
  const verifyCode = useCallback(async (): Promise<boolean> => {
    if (!isLoaded || !signUp || !setActive) return false;
    if (stateRef.current.isLoading) return false;

    const trimmedCode = stateRef.current.code.trim();
    if (!trimmedCode) {
      setState((prev) => ({ ...prev, error: "Please enter the verification code." }));
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await signUp.attemptEmailAddressVerification({
        code: trimmedCode,
      });

      if (result.status === "complete" && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        return true;
      }

      if (result.status === "missing_requirements") {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: `Clerk requires additional fields to complete sign up: ${result.missingFields?.join(", ")}. Please disable these requirements in the Clerk Dashboard, or we must collect them before onboarding.`,
        }));
        return false;
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Verification incomplete. Please try again.",
      }));
      return false;
    } catch (e) {
      const message = getClerkErrorMessage(e, "Invalid verification code. Please try again.");
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      return false;
    }
  }, [isLoaded, signUp, setActive]);

  /**
   * Resend the OTP code.
   */
  const resendCode = useCallback(async () => {
    if (!isLoaded || !signUp) return;
    if (stateRef.current.isLoading) return;

    if (stateRef.current.step !== "verifying") return;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setState((prev) => ({ ...prev, isLoading: false }));
    } catch (e) {
      const message = getClerkErrorMessage(e, "Failed to resend code. Please try again.");
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
    }
  }, [isLoaded, signUp]);

  /**
   * Start OAuth sign-up flow (Google or Microsoft).
   * Redirects to the OAuth provider; user comes back to /sso-callback.
   */
  const startOAuth = useCallback(
    async (strategy: "oauth_google" | "oauth_microsoft") => {
      if (!isLoaded || !signUp) return;
      if (stateRef.current.isLoading) return;

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        await signUp.authenticateWithRedirect({
          strategy,
          redirectUrl: "/sso-callback",
          redirectUrlComplete: "/onboarding",
        });
      } catch (e) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: getClerkErrorMessage(e, "Failed to start sign-up. Please try again."),
        }));
      }
    },
    [isLoaded, signUp],
  );

  /** Go back from code step to email step. */
  const goBack = useCallback(() => {
    setState((prev) => ({ ...prev, step: "email", code: "", error: null }));
  }, []);

  return {
    ...state,
    isReady: isLoaded,
    /** True while the ticket strategy is being processed (show spinner, hide form). */
    isTicketFlow: state.step === "ticket",
    /** Becomes true once the ticket session is active — page should navigate. */
    ticketComplete,
    setEmail,
    setCode,
    sendCode,
    verifyCode,
    resendCode,
    startOAuth,
    goBack,
  };
}
